// Notification utilities for OTP and other alerts

/**
 * Play a notification sound
 * Uses Web Audio API to generate a simple beep sound
 */
export const playNotificationSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for beep sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure beep sound (pleasant notification tone)
    oscillator.frequency.value = 800; // Frequency in Hz
    oscillator.type = 'sine'; // Smooth tone
    
    // Fade in/out for pleasant sound
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    // Play beep
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    // Fallback: try using HTML5 audio if available
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGWi77+efTRAMUKfj8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yk2CBlou+/nn00QDFCn4/C2YxwGOJHX8sx5LAUkd8fw3ZBAC');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignore if audio play fails
      });
    } catch (e) {
      // Silent fail if audio is not supported
    }
  }
};

/**
 * Show browser notification when OTP is sent
 */
export const showOTPNotification = (phoneNumber) => {
  // Play sound
  playNotificationSound();
  
  // Request notification permission if not already granted
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        showNotification(phoneNumber);
      }
    });
  } else if ('Notification' in window && Notification.permission === 'granted') {
    showNotification(phoneNumber);
  }
};

/**
 * Show the actual notification
 */
const showNotification = (phoneNumber) => {
  try {
    const notification = new Notification('OTP Sent Successfully', {
      body: `Verification code has been sent to ${phoneNumber}. Please check your SMS.`,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'otp-sent',
      requireInteraction: false,
      silent: false, // This enables sound for browser notifications
    });
    
    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
    
    // Handle click
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
    console.warn('Could not show browser notification:', error);
  }
};

/**
 * Vibrate device (if supported) when OTP is sent
 */
export const vibrateDevice = () => {
  if ('vibrate' in navigator) {
    try {
      // Short vibration pattern: vibrate for 200ms
      navigator.vibrate(200);
    } catch (error) {
      // Ignore vibration errors
    }
  }
};

/**
 * Complete notification with sound, browser notification, and vibration
 */
export const notifyOTPSent = (phoneNumber) => {
  // Play sound
  playNotificationSound();
  
  // Vibrate (mobile devices)
  vibrateDevice();
  
  // Show browser notification
  showOTPNotification(phoneNumber);
};
